/**
 * Phase 247 — Defensive person-name formatter for display layer.
 *
 * Goal: cleanly correct operator data-entry typos like ``"kalyna"`` →
 * ``"Kalyna"`` *as displayed*, without ever overwriting the user record in
 * the database AND without mangling names whose mixed-case is intentional.
 *
 * Preservation contract:
 *   * ``"DeMarcus"``          → ``"DeMarcus"``    (mixed case = intent)
 *   * ``"McDonald"``          → ``"McDonald"``    (mixed case = intent)
 *   * ``"VBFC"``              → ``"VBFC"``        (all caps = brand)
 *   * ``"Anil Kumar"``        → ``"Anil Kumar"``  (already correct)
 *
 * Auto-correct contract (only when input is entirely lowercase):
 *   * ``"kalyna"``            → ``"Kalyna"``
 *   * ``"mary jane"``         → ``"Mary Jane"``
 *   * ``"mary-jane"``         → ``"Mary-Jane"``    (hyphen-split)
 *   * ``"o'brien"``           → ``"O'Brien"``      (apostrophe-split)
 *   * ``"   anil kumar   "``  → ``"Anil Kumar"``   (trim + collapse)
 *
 * Defensive paths:
 *   * ``null`` / ``undefined`` / ``""`` / non-string → ``""``
 *   * Always returns a string; never throws; safe to drop into JSX directly.
 */

const _titleCaseChunk = (chunk) => {
  if (!chunk) return chunk;
  // Title-case the very first letter AND any letter that follows an
  // apostrophe (so "o'brien" → "O'Brien", "d'souza" → "D'Souza").
  return chunk
    .split("'")
    .map((seg, i) => (seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : seg))
    .join("'");
};

/**
 * Format a display name. See module doc for the full preservation /
 * auto-correct contract.
 *
 * @param {string|null|undefined} name
 * @returns {string} Formatted display name, or `""` when input isn't a usable string.
 */
export const formatPersonName = (name) => {
  if (!name || typeof name !== 'string') return '';

  // Collapse internal whitespace runs and trim edges.
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';

  // Preservation rule — if ANY uppercase character is present, the operator
  // (or the migration that planted the row) made a deliberate casing choice.
  // Don't second-guess it.
  if (trimmed !== trimmed.toLowerCase()) {
    return trimmed;
  }

  // All-lowercase path — title-case each whitespace-separated word, and
  // within each word title-case across hyphens too.
  return trimmed
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map(_titleCaseChunk)
        .join('-')
    )
    .join(' ');
};

export default formatPersonName;
